import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import Select, { components } from 'react-select';
import type { OptionProps, SingleValueProps, GroupBase, StylesConfig } from 'react-select';
import IconSystem from 'icon-collection/icon-systems';

// Inline open-folder icon — IconSystem has folder (closed) and file but no open variant
const FolderOpenIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

// Border colors — match SyutSelect / common-styles.css
const BORDER_COLOR = '#334155';
const BORDER_FOCUS = '#2563eb';
const BORDER_ERROR = '#ef4444';

const sizeConfig = {
  sm: { height: '30px', fontSize: '0.75rem', padding: '0.375rem 0.5rem' },
  md: { height: '36px', fontSize: '0.8rem', padding: '0.5rem 0.75rem' },
  lg: { height: '42px', fontSize: '0.9rem', padding: '0.625rem 1rem' },
};

// ============================================================================
// TYPES
// ============================================================================

export interface TreeSelectOption {
  value: string;
  label: string;
  parentValue?: string | null;
  isDisabled?: boolean;
  sortOrder?: number;
}

interface TreeNode {
  value: string;
  label: string;
  parentValue: string | null;
  isDisabled: boolean;
  sortOrder: number;
  depth: number;
  hasChildren: boolean;
  childCount: number;
  children: TreeNode[];
}

interface FlatTreeOption {
  value: string;
  label: string;
  isDisabled?: boolean;
  __depth: number;
  __hasChildren: boolean;
  __isExpanded: boolean;
  __childCount: number;
  __isLastChild: boolean;
  __parentIsLastFlags: boolean[];
  __isIndeterminate: boolean;
}

export interface SyutTreeSelectProps {
  id?: string;
  name?: string;
  options?: TreeSelectOption[];
  value?: string | string[] | null;
  onChange?: (value: string | string[] | null) => void;
  placeholder?: string;
  label?: string | React.ReactNode;
  error?: string;
  helperText?: string;
  required?: boolean;
  className?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  menuPortalTarget?: HTMLElement | null;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  maxMenuHeight?: number;
  noOptionsMessage?: string;
  isMulti?: boolean;
  isAsync?: boolean;
  loadOptions?: (inputValue: string) => Promise<TreeSelectOption[]>;
  defaultOptions?: boolean | TreeSelectOption[];
  cacheOptions?: boolean;
  seedOptions?: TreeSelectOption[];
  debounceDelay?: number;
  minSearchLength?: number;
  defaultExpandedValues?: string[];
  expandAllOnSearch?: boolean;
  showChildCount?: boolean;
  excludeValue?: string | null;
  showConnectorLines?: boolean;
  showNodeIcons?: boolean;
  selectionMode?: 'independent' | 'cascading';
  animated?: boolean;
}

// ============================================================================
// CSS INJECTION (HMR-safe)
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'syut-tree-select-styles';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    .syut-tree-option {
      display: flex;
      align-items: center;
      gap: 0.125rem;
      cursor: pointer;
      border-radius: 4px;
      padding: 0.375rem 0.5rem;
      transition: background 0.15s ease;
      font-size: inherit;
    }
    .syut-tree-option.has-connectors {
      align-items: stretch;
      padding-left: 0.25rem;
    }
    .syut-tree-option.is-focused {
      background: var(--bg-secondary, #f8fafc);
    }
    .syut-tree-option.is-selected {
      background: var(--primary, #3b82f6);
      color: white;
    }
    .syut-tree-option.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    /* Content wrapper for vertical centering with stretch connectors */
    .syut-tree-option-content {
      display: flex;
      align-items: center;
      gap: 0.125rem;
      flex: 1;
      min-width: 0;
    }
    /* Connector lines */
    .syut-tree-connector {
      display: inline-flex;
      width: 1.25rem;
      flex-shrink: 0;
      position: relative;
      align-self: stretch;
      min-height: 1.75rem;
    }
    .connector-pass::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .connector-mid::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .connector-mid::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 50%;
      height: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .connector-last::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      height: 50%;
      width: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .connector-last::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 50%;
      height: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .syut-tree-option.is-selected .syut-tree-connector::before,
    .syut-tree-option.is-selected .syut-tree-connector::after {
      background: rgba(255, 255, 255, 0.35);
    }
    /* Chevron with rotation animation */
    .syut-tree-chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
      color: var(--text-muted, #64748b);
      border-radius: 3px;
      transition: background 0.15s ease;
    }
    .syut-tree-chevron svg {
      transition: transform 0.2s ease;
    }
    .syut-tree-chevron.is-expanded svg {
      transform: rotate(90deg);
    }
    .syut-tree-chevron.has-children {
      cursor: pointer;
    }
    .syut-tree-chevron.has-children:hover {
      background: var(--bg-tertiary, #e2e8f0);
    }
    .syut-tree-option.is-selected .syut-tree-chevron {
      color: rgba(255, 255, 255, 0.8);
    }
    .syut-tree-option.is-selected .syut-tree-chevron.has-children:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    /* Node icons */
    .syut-tree-node-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.125rem;
      flex-shrink: 0;
      margin-right: 0.25rem;
      color: var(--text-muted, #64748b);
    }
    .syut-tree-node-icon.is-folder {
      color: var(--tree-folder-color, #eab308);
    }
    .syut-tree-option.is-selected .syut-tree-node-icon {
      color: rgba(255, 255, 255, 0.8);
    }
    /* Checkbox */
    .syut-tree-checkbox {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
      border: 1.5px solid var(--border-color, #334155);
      border-radius: 3px;
      margin-right: 0.25rem;
      transition: all 0.15s ease;
    }
    .syut-tree-checkbox.checked {
      background: var(--primary, #3b82f6);
      border-color: var(--primary, #3b82f6);
      color: white;
    }
    .syut-tree-checkbox.indeterminate {
      background: var(--primary, #3b82f6);
      border-color: var(--primary, #3b82f6);
    }
    .syut-tree-checkbox-minus {
      display: block;
      width: 8px;
      height: 2px;
      background: white;
      border-radius: 1px;
    }
    .syut-tree-option.is-selected .syut-tree-checkbox {
      border-color: rgba(255, 255, 255, 0.6);
    }
    .syut-tree-label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .syut-tree-count {
      font-size: 0.7em;
      color: var(--text-muted, #64748b);
      flex-shrink: 0;
      margin-left: 0.25rem;
    }
    .syut-tree-option.is-selected .syut-tree-count {
      color: rgba(255, 255, 255, 0.7);
    }
    .syut-tree-highlight {
      background: rgba(250, 204, 21, 0.35);
      border-radius: 2px;
      padding: 0 1px;
    }
    .syut-tree-option.is-selected .syut-tree-highlight {
      background: rgba(255, 255, 255, 0.25);
    }
    /* Fade-in animation for expanded children */
    @keyframes syut-tree-fadein {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .syut-tree-option.is-animated {
      animation: syut-tree-fadein 0.15s ease-out;
    }
  `;
}

// ============================================================================
// REACT-SELECT STYLES (mirrors SyutSelect)
// ============================================================================

const getTreeSelectStyles = (
  size: 'sm' | 'md' | 'lg',
  hasError: boolean,
): StylesConfig<FlatTreeOption, boolean, GroupBase<FlatTreeOption>> => {
  const config = sizeConfig[size];
  return {
    control: (base, state) => ({
      ...base,
      minHeight: config.height,
      fontSize: config.fontSize,
      outline: `1px solid ${hasError ? BORDER_ERROR : state.isFocused ? BORDER_FOCUS : BORDER_COLOR}`,
      outlineOffset: '-1px',
      boxShadow: state.isFocused ? `0 0 0 2px ${hasError ? BORDER_ERROR : BORDER_FOCUS}` : 'none',
      backgroundColor: state.isDisabled ? 'var(--bg-secondary)' : 'var(--bg-primary)',
      '&:hover': { outline: `1px solid ${hasError ? BORDER_ERROR : BORDER_FOCUS}` },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 10200,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 10200 }),
    menuList: (base) => ({ ...base, padding: '4px' }),
    // Option styles handled by custom TreeOption component — override react-select defaults
    option: () => ({}),
    singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
    multiValue: (base) => ({ ...base, backgroundColor: 'var(--bg-tertiary, #e0ebf7)', borderRadius: '4px' }),
    multiValueLabel: (base) => ({ ...base, color: 'var(--text-primary)', fontSize: config.fontSize, padding: '2px 6px' }),
    multiValueRemove: (base) => ({
      ...base,
      color: 'var(--text-muted)',
      '&:hover': { backgroundColor: 'var(--danger, #ef4444)', color: 'white' },
    }),
    placeholder: (base) => ({ ...base, color: 'var(--text-muted)' }),
    input: (base) => ({ ...base, color: 'var(--text-primary)' }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: '6px',
      color: 'var(--text-muted)',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
      transition: 'transform 0.2s ease',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '6px',
      color: 'var(--text-muted)',
      '&:hover': { color: 'var(--danger, #ef4444)' },
    }),
    indicatorSeparator: (base, state) => ({
      ...base,
      display: state.isMulti ? 'none' : base.display,
    }),
  };
};

// ============================================================================
// TREE UTILITY FUNCTIONS
// ============================================================================

function buildTree(
  options: TreeSelectOption[],
  excludeValue?: string | null,
): { roots: TreeNode[]; nodeMap: Map<string, TreeNode> } {
  const lookup = new Map<string, TreeSelectOption>();
  for (const opt of options) lookup.set(opt.value, opt);

  // Collect values to exclude (node + all descendants)
  const excludeSet = new Set<string>();
  if (excludeValue && lookup.has(excludeValue)) {
    const collectDescendants = (val: string) => {
      excludeSet.add(val);
      for (const opt of options) {
        if (opt.parentValue === val && !excludeSet.has(opt.value)) {
          collectDescendants(opt.value);
        }
      }
    };
    collectDescendants(excludeValue);
  }

  // Group by parentValue
  const childrenMap = new Map<string | null, TreeSelectOption[]>();
  for (const opt of options) {
    if (excludeSet.has(opt.value)) continue;
    const parent = opt.parentValue ?? null;
    // If parent is excluded or doesn't exist in lookup, treat as root
    const effectiveParent = parent && lookup.has(parent) && !excludeSet.has(parent) ? parent : null;
    if (!childrenMap.has(effectiveParent)) childrenMap.set(effectiveParent, []);
    childrenMap.get(effectiveParent)!.push(opt);
  }

  // Sort siblings by sortOrder then label
  for (const [, children] of childrenMap) {
    children.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
  }

  const nodeMap = new Map<string, TreeNode>();

  // Recursive build with circular reference protection
  function buildNodes(parentValue: string | null, depth: number, visited: Set<string>): TreeNode[] {
    const children = childrenMap.get(parentValue) || [];
    return children.map(opt => {
      if (visited.has(opt.value)) {
        // Circular reference detected — return as leaf
        const node: TreeNode = {
          value: opt.value, label: opt.label, parentValue: opt.parentValue ?? null,
          isDisabled: opt.isDisabled ?? false, sortOrder: opt.sortOrder ?? 0,
          depth, hasChildren: false, childCount: 0, children: [],
        };
        nodeMap.set(opt.value, node);
        return node;
      }
      visited.add(opt.value);
      const childNodes = buildNodes(opt.value, depth + 1, visited);
      const node: TreeNode = {
        value: opt.value, label: opt.label, parentValue: opt.parentValue ?? null,
        isDisabled: opt.isDisabled ?? false, sortOrder: opt.sortOrder ?? 0,
        depth, hasChildren: childNodes.length > 0,
        childCount: childNodes.length, children: childNodes,
      };
      nodeMap.set(opt.value, node);
      return node;
    });
  }

  const roots = buildNodes(null, 0, new Set());
  return { roots, nodeMap };
}

function getSearchExpandedSet(roots: TreeNode[], query: string): Set<string> {
  const expanded = new Set<string>();
  const lowerQuery = query.toLowerCase();

  function collect(node: TreeNode): boolean {
    let anyChildMatch = false;
    for (const child of node.children) {
      if (collect(child)) anyChildMatch = true;
    }
    const selfMatch = node.label.toLowerCase().includes(lowerQuery);
    if (selfMatch || anyChildMatch) {
      if (node.hasChildren) expanded.add(node.value);
      return true;
    }
    return false;
  }

  for (const root of roots) collect(root);
  return expanded;
}

function getMatchingPaths(roots: TreeNode[], query: string): Set<string> {
  const paths = new Set<string>();
  const lowerQuery = query.toLowerCase();

  function collect(node: TreeNode): boolean {
    let anyChildMatch = false;
    for (const child of node.children) {
      if (collect(child)) anyChildMatch = true;
    }
    const selfMatch = node.label.toLowerCase().includes(lowerQuery);
    if (selfMatch || anyChildMatch) {
      paths.add(node.value);
      return true;
    }
    return false;
  }

  for (const root of roots) collect(root);
  return paths;
}

function flattenTree(
  roots: TreeNode[],
  expandedSet: Set<string>,
  indeterminateSet?: Set<string>,
): FlatTreeOption[] {
  const result: FlatTreeOption[] = [];
  function walk(node: TreeNode, isLastChild: boolean, parentIsLastFlags: boolean[]) {
    const isExpanded = expandedSet.has(node.value);
    result.push({
      value: node.value,
      label: node.label,
      isDisabled: node.isDisabled,
      __depth: node.depth,
      __hasChildren: node.hasChildren,
      __isExpanded: isExpanded,
      __childCount: node.childCount,
      __isLastChild: isLastChild,
      __parentIsLastFlags: [...parentIsLastFlags],
      __isIndeterminate: indeterminateSet?.has(node.value) ?? false,
    });
    if (isExpanded) {
      const newFlags = [...parentIsLastFlags, isLastChild];
      node.children.forEach((child, i) =>
        walk(child, i === node.children.length - 1, newFlags),
      );
    }
  }
  roots.forEach((root, i) => walk(root, i === roots.length - 1, []));
  return result;
}

// ============================================================================
// CASCADING SELECTION UTILITIES
// ============================================================================

function getAllDescendantValues(nodeValue: string, nodeMap: Map<string, TreeNode>): string[] {
  const result: string[] = [];
  const node = nodeMap.get(nodeValue);
  if (!node) return result;
  function collect(n: TreeNode) {
    for (const child of n.children) {
      result.push(child.value);
      collect(child);
    }
  }
  collect(node);
  return result;
}

function getAllAncestorValues(nodeValue: string, nodeMap: Map<string, TreeNode>): string[] {
  const result: string[] = [];
  let current = nodeMap.get(nodeValue);
  while (current && current.parentValue) {
    result.push(current.parentValue);
    current = nodeMap.get(current.parentValue);
  }
  return result;
}

function computeIndeterminateSet(
  selectedValues: Set<string>,
  roots: TreeNode[],
): Set<string> {
  const indeterminate = new Set<string>();
  function dfs(node: TreeNode): { total: number; selected: number } {
    if (!node.hasChildren) {
      return { total: 1, selected: selectedValues.has(node.value) ? 1 : 0 };
    }
    let total = 0;
    let selected = 0;
    for (const child of node.children) {
      const counts = dfs(child);
      total += counts.total;
      selected += counts.selected;
    }
    if (selected > 0 && selected < total) {
      indeterminate.add(node.value);
    }
    return { total, selected: selectedValues.has(node.value) ? total : selected };
  }
  for (const root of roots) dfs(root);
  return indeterminate;
}

// ============================================================================
// HIGHLIGHT HELPER
// ============================================================================

function highlightLabel(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="syut-tree-highlight">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

// ============================================================================
// CUSTOM REACT-SELECT COMPONENTS
// ============================================================================

const TreeOption: React.FC<OptionProps<FlatTreeOption, boolean>> = (props) => {
  const { data, innerRef, innerProps, isSelected, isFocused } = props;
  const { __depth, __hasChildren, __isExpanded, __childCount, __isLastChild, __parentIsLastFlags, __isIndeterminate } = data;
  const selectProps = props.selectProps as any;
  const onToggleExpand = selectProps.__onToggleExpand as ((value: string) => void) | undefined;
  const showChildCount = selectProps.__showChildCount as boolean;
  const searchQuery = selectProps.__searchQuery as string;
  const isMulti = selectProps.isMulti as boolean;
  const showConnectorLines = selectProps.__showConnectorLines as boolean;
  const showNodeIcons = selectProps.__showNodeIcons as boolean;
  const animated = selectProps.__animated as boolean;

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand?.(data.value);
  };

  const cls = [
    'syut-tree-option',
    isFocused && 'is-focused',
    isSelected && 'is-selected',
    data.isDisabled && 'is-disabled',
    showConnectorLines && 'has-connectors',
    animated && __depth > 0 && 'is-animated',
  ].filter(Boolean).join(' ');

  // Build connector spans for each ancestor depth level
  const connectors: React.ReactNode[] = [];
  if (showConnectorLines && __depth > 0) {
    for (let i = 0; i < __depth - 1; i++) {
      const isAncestorLast = __parentIsLastFlags[i + 1];
      connectors.push(
        <span key={`c${i}`} className={`syut-tree-connector ${isAncestorLast ? 'connector-blank' : 'connector-pass'}`} />,
      );
    }
    // Last column: L-shape (last child) or T-shape (middle child)
    connectors.push(
      <span key="cl" className={`syut-tree-connector ${__isLastChild ? 'connector-last' : 'connector-mid'}`} />,
    );
  }

  const showIndeterminate = __isIndeterminate && !isSelected;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={cls}
      style={!showConnectorLines ? { paddingLeft: `${0.5 + __depth * 1.25}rem` } : undefined}
    >
      {connectors}
      <div className="syut-tree-option-content">
        {isMulti && (
          <span className={`syut-tree-checkbox${isSelected ? ' checked' : ''}${showIndeterminate ? ' indeterminate' : ''}`}>
            {isSelected && <IconSystem.check size={10} />}
            {showIndeterminate && <span className="syut-tree-checkbox-minus" />}
          </span>
        )}
        <span
          className={`syut-tree-chevron${__hasChildren ? ' has-children' : ''}${__isExpanded ? ' is-expanded' : ''}`}
          onClick={__hasChildren ? handleChevronClick : undefined}
          onMouseDown={__hasChildren ? (e) => e.preventDefault() : undefined}
        >
          {__hasChildren && <IconSystem.chevronRight size={12} />}
        </span>
        {showNodeIcons && (
          <span className={`syut-tree-node-icon${__hasChildren ? ' is-folder' : ''}`}>
            {__hasChildren
              ? (__isExpanded ? <FolderOpenIcon size={14} /> : <IconSystem.folder size={14} />)
              : <IconSystem.file size={14} />}
          </span>
        )}
        <span className="syut-tree-label">
          {highlightLabel(data.label, searchQuery || '')}
        </span>
        {showChildCount && __hasChildren && (
          <span className="syut-tree-count">({__childCount})</span>
        )}
      </div>
    </div>
  );
};

const TreeSingleValue: React.FC<SingleValueProps<FlatTreeOption, boolean>> = (props) => (
  <components.SingleValue {...props}>{props.data.label}</components.SingleValue>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const SyutTreeSelect: React.FC<SyutTreeSelectProps> = ({
  id, name, options: propOptions = [], value, onChange, placeholder = 'Select...',
  label, error, helperText, required = false, className = '',
  isDisabled = false, isClearable = true, size = 'md',
  menuPortalTarget, menuPlacement = 'auto', maxMenuHeight = 300,
  noOptionsMessage = 'No matching items',
  isMulti = false,
  isAsync = false, loadOptions, defaultOptions, cacheOptions: _cacheOptions = true,
  seedOptions, debounceDelay = 300, minSearchLength = 1,
  defaultExpandedValues, expandAllOnSearch = true,
  showChildCount = false, excludeValue,
  showConnectorLines = true, showNodeIcons = true,
  selectionMode = 'independent', animated = true,
}) => {
  // --- State ---
  const [expandedValues, setExpandedValues] = useState<Set<string>>(
    new Set(defaultExpandedValues || []),
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Async state
  const [asyncOptions, setAsyncOptions] = useState<TreeSelectOption[]>(
    Array.isArray(defaultOptions) ? defaultOptions : [],
  );
  const asyncCachedOptionsRef = useRef<TreeSelectOption[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Initial async load
  useEffect(() => {
    if (!isAsync || !loadOptions) return;
    if (defaultOptions === true || defaultOptions === undefined) {
      (async () => {
        setAsyncLoading(true);
        try {
          const result = await loadOptions('');
          if (mountedRef.current) {
            setAsyncOptions(result || []);
            asyncCachedOptionsRef.current = result || [];
          }
        } finally {
          if (mountedRef.current) setAsyncLoading(false);
        }
      })();
    }
  }, [isAsync, loadOptions, defaultOptions]);

  // Cleanup debounce
  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  // The options source: async mode uses asyncOptions, sync mode uses propOptions
  const effectiveOptions = isAsync ? asyncOptions : propOptions;

  // --- Stage 1: Build tree (memoized on options + excludeValue) ---
  const { roots, nodeMap } = useMemo(
    () => buildTree(effectiveOptions, excludeValue),
    [effectiveOptions, excludeValue],
  );

  // --- Stage 2: Compute effective expanded set ---
  const effectiveExpanded = useMemo(() => {
    if (!searchQuery.trim() || !expandAllOnSearch) return expandedValues;
    const searchExpanded = getSearchExpandedSet(roots, searchQuery);
    return new Set([...expandedValues, ...searchExpanded]);
  }, [expandedValues, searchQuery, roots, expandAllOnSearch]);

  // --- Stage 2b: Compute indeterminate set for cascading mode ---
  const indeterminateSet = useMemo(() => {
    if (selectionMode !== 'cascading' || !isMulti || !value) return undefined;
    const selectedSet = new Set(Array.isArray(value) ? value : [value]);
    return computeIndeterminateSet(selectedSet, roots);
  }, [selectionMode, isMulti, value, roots]);

  // --- Stage 3: Flatten to visible options ---
  const visibleOptions = useMemo(() => {
    const flat = flattenTree(roots, effectiveExpanded, indeterminateSet);
    if (!searchQuery.trim()) return flat;
    const matchingPaths = getMatchingPaths(roots, searchQuery);
    return flat.filter(opt => matchingPaths.has(opt.value));
  }, [roots, effectiveExpanded, searchQuery, indeterminateSet]);

  // --- Value resolution ---
  const selectValue = useMemo(() => {
    if (value === null || value === undefined || value === '') {
      return isMulti ? [] : null;
    }

    const defaultFlat = { __isLastChild: false, __parentIsLastFlags: [] as boolean[], __isIndeterminate: false };

    // Build resolution pool: all nodes + seed options
    const resolveLabel = (val: string): FlatTreeOption => {
      const node = nodeMap.get(val);
      if (node) {
        return {
          value: node.value, label: node.label, __depth: node.depth,
          __hasChildren: node.hasChildren, __isExpanded: false, __childCount: node.childCount,
          ...defaultFlat,
        };
      }
      // Check seed options
      const seed = seedOptions?.find(s => s.value === val);
      if (seed) {
        return {
          value: seed.value, label: seed.label, __depth: 0,
          __hasChildren: false, __isExpanded: false, __childCount: 0,
          ...defaultFlat,
        };
      }
      // Check async cache
      const cached = asyncCachedOptionsRef.current.find(c => c.value === val);
      if (cached) {
        return {
          value: cached.value, label: cached.label, __depth: 0,
          __hasChildren: false, __isExpanded: false, __childCount: 0,
          ...defaultFlat,
        };
      }
      // Fallback
      return {
        value: val, label: val, __depth: 0,
        __hasChildren: false, __isExpanded: false, __childCount: 0,
        ...defaultFlat,
      };
    };

    if (isMulti) {
      const values = Array.isArray(value) ? value : [value];
      return values.map(resolveLabel);
    }
    const singleVal = Array.isArray(value) ? value[0] : value;
    return resolveLabel(singleVal);
  }, [value, isMulti, nodeMap, seedOptions]);

  // --- Handlers ---
  const handleToggleExpand = useCallback((nodeValue: string) => {
    setExpandedValues(prev => {
      const next = new Set(prev);
      if (next.has(nodeValue)) next.delete(nodeValue);
      else next.add(nodeValue);
      return next;
    });
  }, []);

  const handleChange = useCallback((newValue: any) => {
    if (!onChange) return;

    if (!isMulti) {
      onChange(newValue ? (newValue as FlatTreeOption).value : null);
      return;
    }

    if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
      onChange(null);
      return;
    }

    const nextValues = (newValue as FlatTreeOption[]).map(v => v.value);

    // Independent mode: pass values as-is
    if (selectionMode !== 'cascading') {
      onChange(nextValues);
      return;
    }

    // Cascading mode: auto-select/deselect descendants and manage ancestors
    const prevSet = new Set(Array.isArray(value) ? value : value ? [value] : []);
    const nextSet = new Set(nextValues);

    const added = nextValues.filter(v => !prevSet.has(v));
    const removed = [...prevSet].filter(v => !nextSet.has(v));

    const finalSet = new Set(nextSet);

    // For each added node: also add all descendants, then check ancestors
    for (const v of added) {
      getAllDescendantValues(v, nodeMap).forEach(d => finalSet.add(d));
      for (const ancestor of getAllAncestorValues(v, nodeMap)) {
        const ancestorNode = nodeMap.get(ancestor);
        if (ancestorNode && ancestorNode.children.every(c => finalSet.has(c.value))) {
          finalSet.add(ancestor);
        }
      }
    }

    // For each removed node: remove all descendants + uncheck ancestors
    for (const v of removed) {
      finalSet.delete(v);
      getAllDescendantValues(v, nodeMap).forEach(d => finalSet.delete(d));
      getAllAncestorValues(v, nodeMap).forEach(a => finalSet.delete(a));
    }

    onChange(finalSet.size > 0 ? [...finalSet] : null);
  }, [onChange, isMulti, selectionMode, value, nodeMap]);

  const handleInputChange = useCallback((inputValue: string, meta: { action: string }) => {
    if (meta.action === 'input-change') {
      setSearchQuery(inputValue);
      // Async: trigger loadOptions for typed input
      if (isAsync && loadOptions && inputValue.trim()) {
        if (inputValue.trim().length < minSearchLength) return;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(async () => {
          setAsyncLoading(true);
          try {
            const result = await loadOptions(inputValue);
            if (mountedRef.current) setAsyncOptions(result || []);
          } finally {
            if (mountedRef.current) setAsyncLoading(false);
          }
        }, debounceDelay);
      } else if (isAsync && loadOptions && !inputValue.trim()) {
        // Restore cached default options when search cleared
        setAsyncOptions(asyncCachedOptionsRef.current);
      }
    }
    if (meta.action === 'menu-close') {
      setSearchQuery('');
      // Restore default async options on close
      if (isAsync && asyncCachedOptionsRef.current.length > 0) {
        setAsyncOptions(asyncCachedOptionsRef.current);
      }
    }
  }, [isAsync, loadOptions, debounceDelay, minSearchLength]);

  // Keyboard: Right arrow expand, Left arrow collapse
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Get the currently focused option value from react-select's internal state
    const selectEl = e.currentTarget as HTMLElement;
    const focused = selectEl.querySelector('.syut-tree-option.is-focused') as HTMLElement;
    if (!focused) return;

    // Extract value from the focused option — find it by matching in visibleOptions
    const focusedIndex = Array.from(
      selectEl.querySelectorAll('.syut-tree-option'),
    ).indexOf(focused);
    if (focusedIndex < 0 || focusedIndex >= visibleOptions.length) return;
    const focusedOption = visibleOptions[focusedIndex];

    if (e.key === 'ArrowRight' && focusedOption.__hasChildren && !focusedOption.__isExpanded) {
      e.preventDefault();
      handleToggleExpand(focusedOption.value);
    } else if (e.key === 'ArrowLeft' && focusedOption.__hasChildren && focusedOption.__isExpanded) {
      e.preventDefault();
      handleToggleExpand(focusedOption.value);
    }
  }, [visibleOptions, handleToggleExpand]);

  // --- Styles ---
  const customStyles = useMemo(() => getTreeSelectStyles(size, !!error), [size, error]);

  const customComponents = useMemo(() => ({
    Option: TreeOption,
    SingleValue: TreeSingleValue,
  }), []);

  // We always use Select (not AsyncSelect) because we manage async loading ourselves
  // This gives us full control over the options array for tree building
  return (
    <div className={`syut-select-container ${className}`}>
      {label && (
        <label className="syut-select-label" htmlFor={id}>
          {label}
          {required && <span className="syut-select-required">*</span>}
        </label>
      )}

      <Select<FlatTreeOption, boolean, GroupBase<FlatTreeOption>>
        inputId={id}
        name={name}
        options={visibleOptions}
        value={selectValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={asyncLoading}
        isClearable={isClearable}
        isMulti={isMulti as any}
        isSearchable
        styles={customStyles}
        components={customComponents}
        classNamePrefix="syut-select"
        className={error ? 'syut-select-has-error' : ''}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        closeMenuOnSelect={!isMulti}
        filterOption={() => true}
        noOptionsMessage={() => noOptionsMessage}
        getOptionValue={(opt) => opt.value}
        getOptionLabel={(opt) => opt.label}
        // Pass tree state via selectProps
        {...{
          __onToggleExpand: handleToggleExpand,
          __showChildCount: showChildCount,
          __searchQuery: searchQuery,
          __showConnectorLines: showConnectorLines,
          __showNodeIcons: showNodeIcons,
          __animated: animated,
        } as any}
      />

      {error && <span className="syut-select-error">{error}</span>}
      {helperText && !error && <span className="syut-select-helper">{helperText}</span>}
    </div>
  );
};

export default SyutTreeSelect;
